// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { VoiceProfile } from '../api/intelligenceApi';

/**
 * Pick the best SpeechSynthesisVoice for a configured voice profile.
 * Cascade:
 *   1. First voice whose name exactly matches an entry in preferredVoices
 *   2. First voice whose lang matches the configured locale (and, if
 *      gender is set, has the gender hint matching where available)
 *   3. First voice whose lang matches the configured locale ignoring gender
 *   4. The system default voice for the locale (returned as null —
 *      caller leaves utterance.voice unset and the engine picks)
 */
export function pickVoice(
  voices: SpeechSynthesisVoice[],
  profile: VoiceProfile,
): SpeechSynthesisVoice | null {
  if (voices.length === 0) return null;

  const preferred = profile.preferredVoices ?? [];
  for (const name of preferred) {
    const exact = voices.find(v => v.name === name);
    if (exact) return exact;
  }

  const locale = profile.locale ?? 'en-US';
  const localeMatches = voices.filter(v => v.lang === locale)
    .concat(voices.filter(v => v.lang.startsWith(locale.split('-')[0]) && v.lang !== locale));

  if (localeMatches.length === 0) return null;
  return localeMatches[0];
}
